import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionHeading from "@/components/base/SectionHeading";
import { Skeleton } from "@/components/base/Loading";
import { formatNumber, formatRelativeTime } from "@/lib/format";
import { exportLicenseReport } from "@/lib/exportReport";
import type { ActivityLog, PlatformStats } from "@/pages/home/hooks/useHomeData";

interface ActivityFeedProps {
  activities: ActivityLog[];
  stats: PlatformStats | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const levelStyles: Record<string, { dot: string; badge: string; labelKey: string }> = {
  success: { dot: "bg-primary-500", badge: "bg-primary-100 text-primary-700", labelKey: "activity.levelSuccess" },
  warning: { dot: "bg-accent-400", badge: "bg-accent-100 text-accent-700", labelKey: "activity.levelWarning" },
  error: { dot: "bg-accent-600", badge: "bg-accent-100 text-accent-800", labelKey: "activity.levelError" },
  info: { dot: "bg-secondary-400", badge: "bg-secondary-100 text-secondary-900", labelKey: "activity.levelInfo" },
};

export default function ActivityFeed({ activities, stats, loading, error, onRetry }: ActivityFeedProps) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [exportFailed, setExportFailed] = useState(false);

  const filters = [
    { key: "all", label: t("activity.filterAll") },
    { key: "success", label: t("activity.levelSuccess") },
    { key: "warning", label: t("activity.levelWarning") },
    { key: "error", label: t("activity.levelError") },
    { key: "info", label: t("activity.levelInfo") },
  ];

  const quickEntries = [
    { label: t("activity.quickApply"), icon: "ri-file-add-line", to: "/apply" },
    { label: t("activity.quickProducts"), icon: "ri-apps-2-line", to: "/products" },
    { label: t("activity.quickLicenses"), icon: "ri-key-2-line", to: "/licenses" },
    { label: t("activity.quickMonitor"), icon: "ri-line-chart-line", href: "#status" },
    { label: t("activity.quickHelp"), icon: "ri-question-line", to: "/help" },
  ];

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: activities.length };
    activities.forEach((item) => {
      result[item.level] = (result[item.level] ?? 0) + 1;
    });
    return result;
  }, [activities]);

  const visibleActivities = useMemo(
    () => (activeFilter === "all" ? activities : activities.filter((item) => item.level === activeFilter)),
    [activities, activeFilter],
  );

  const handleExport = async () => {
    setExporting(true);
    setExportMsg(null);
    setExportFailed(false);
    try {
      const count = await exportLicenseReport();
      setExportMsg(t("activity.exportedToast", { num: count }));
    } catch (err) {
      setExportFailed(true);
      setExportMsg(err instanceof Error ? err.message : t("activity.exportFailed"));
    } finally {
      setExporting(false);
      window.setTimeout(() => setExportMsg(null), 4000);
    }
  };

  return (
    <section id="activity" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow={t("activity.eyebrow")}
          title={t("activity.title")}
          description={t("activity.description")}
        />

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
              <h3 className="font-heading text-base font-semibold text-foreground-950">{t("activity.eventStream")}</h3>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500" />
                  </span>
                  {t("activity.realtime")}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    void handleExport();
                  }}
                  disabled={exporting}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={exporting ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />
                  </span>
                  {exporting ? t("activity.exporting") : t("activity.exportBtn")}
                </button>
              </div>
            </div>

            {exportMsg && (
              <div
                className={`flex items-center gap-2 border-b border-background-200 px-5 py-2.5 text-xs ${
                  exportFailed ? "bg-accent-50 text-accent-800" : "bg-primary-50 text-primary-700"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={exportFailed ? "ri-error-warning-line" : "ri-checkbox-circle-line"} />
                </span>
                {exportMsg}
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-b border-background-200 px-5 py-3">
              {filters.map((filter) => {
                const active = activeFilter === filter.key;
                const count = counts[filter.key] ?? 0;
                return (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => setActiveFilter(filter.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {filter.label}
                    <span className={active ? "ml-1 text-background-100" : "ml-1 text-foreground-400"}>{count}</span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="space-y-4 p-5">
                {[0, 1, 2, 3, 4].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Skeleton className="mt-1 h-3 w-3 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-error-warning-line text-xl" />
                </span>
                <p className="text-sm text-foreground-600">{error}</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  {t("activity.reload")}
                </button>
              </div>
            ) : visibleActivities.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-foreground-500">{t("activity.empty")}</div>
            ) : (
              <ul className="max-h-[460px] divide-y divide-background-200 overflow-y-auto">
                {visibleActivities.map((item) => {
                  const style = levelStyles[item.level] ?? levelStyles.info;
                  return (
                    <li key={item.id} className="flex items-start gap-3 px-5 py-4 transition-colors hover:bg-background-100">
                      <span className="mt-1.5 flex h-3 w-3 items-center justify-center">
                        <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground-950">{item.action}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] ${style.badge}`}>{t(style.labelKey)}</span>
                        </div>
                        {item.detail && <p className="mt-1 text-xs text-foreground-600">{item.detail}</p>}
                        <p className="mt-1 text-xs text-foreground-400">
                          {item.actor_name || t("activity.system")} · {formatRelativeTime(item.created_at)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-primary-950 p-5 text-background-100">
              <p className="text-xs text-background-300">{t("activity.pendingTitle")}</p>
              <p className="mt-2 font-heading text-4xl font-semibold text-background-50">
                {loading ? "--" : formatNumber(stats?.pending_applications)}
              </p>
              <p className="mt-2 text-xs text-background-300">{t("activity.pendingSub")}</p>
              <Link
                to="/auth"
                className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-shield-user-line" />
                </span>
                {t("activity.goAdmin")}
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">{t("activity.quickTitle")}</h3>
              <ul className="mt-3 space-y-1">
                {quickEntries.map((entry) => (
                  <li key={entry.label}>
                    {"to" in entry && entry.to ? (
                      <Link
                        to={entry.to}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                      >
                        <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                          <i className={entry.icon} />
                        </span>
                        {entry.label}
                      </Link>
                    ) : (
                      <a
                        href={entry.href}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                      >
                        <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                          <i className={entry.icon} />
                        </span>
                        {entry.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}